import {
  asClause,
  fillFrame,
  properWords,
  tidy,
} from "@/lib/tool-kit/essay/sentence";

/**
 * Theme statements and the checks that separate a theme from a topic, a summary, or a
 * moral.
 *
 * English teachers draw the line the same way: a *topic* is a word or phrase
 * ("ambition"); a *theme* is a full-sentence claim about it ("Ambition can blind people
 * to what it costs"). A theme is general enough to apply beyond one text, so it does not
 * name the characters, and it states an idea about people rather than a plot event, an
 * instruction, or a cliché. The frames hedge ("can", "may", "tends to") because a claim
 * that says "always" or "everyone" fails at the first counterexample.
 *
 * The checks are string heuristics. They catch the common slips and cannot judge whether
 * a theme is true or fits the text, so the labels say "check" and not "score".
 */

export type ThemeModal = "can" | "may" | "tends";

export const THEME_MODALS: readonly ThemeModal[] = ["can", "may", "tends"];

export interface ThemeInput {
  title: string;
  author: string;
  /** Character names, separated by commas, semicolons, or "and". */
  characters: string;
  /** The abstract noun the theme is about: "guilt". */
  topic: string;
  /** What the text says about it, starting with a bare verb: "turn a hidden crime…". */
  predicate: string;
  /** Optional: the circumstance, as a clause — "people try to bury what they have done". */
  circumstance: string;
  /** Optional: the common belief the text complicates, as a clause. */
  commonView: string;
  modal: ThemeModal;
}

export const EMPTY_THEME: ThemeInput = {
  title: "",
  author: "",
  characters: "",
  topic: "",
  predicate: "",
  circumstance: "",
  commonView: "",
  modal: "can",
};

/** Frames; `{modal}` is "can", "may" or "tends to". Empty optional fields drop a frame. */
export const THEME_FRAMES = [
  "{topic} {modal} {predicate}.",
  "When {circumstance}, {topic} {modal} {predicate}.",
  "Although {commonView}, {topic} {modal} {predicate}.",
] as const;

export interface ThemeLabels {
  /** The words for each modal: { can: "can", may: "may", tends: "tends to" }. */
  modals: Readonly<Record<ThemeModal, string>>;
  frames?: readonly string[];
}

/** Statements the input can build, in frame order. Needs a topic and a predicate. */
export function buildThemeStatements(
  input: ThemeInput,
  labels: ThemeLabels
): string[] {
  if (tidy(input.topic) === "" || tidy(input.predicate) === "") {
    return [];
  }
  const proper = properWords([
    input.title,
    input.author,
    input.characters,
    input.circumstance,
    input.commonView,
  ]);
  const optional = (text: string) =>
    tidy(text) === "" ? null : asClause(text, proper);
  const values: Record<string, string | null> = {
    topic: asClause(input.topic, proper),
    modal: labels.modals[input.modal],
    predicate: asClause(input.predicate, proper),
    circumstance: optional(input.circumstance),
    commonView: optional(input.commonView),
  };
  return (labels.frames ?? THEME_FRAMES)
    .map((frame) => fillFrame(frame, values))
    .filter((text): text is string => text !== null);
}

/* ------------------------------------------------------------------ checks */

export type ThemeCheckId =
  | "length"
  | "names"
  | "command"
  | "absolute"
  | "summary"
  | "cliche"
  | "topic";

export const THEME_CHECK_IDS: readonly ThemeCheckId[] = [
  "length",
  "names",
  "command",
  "absolute",
  "summary",
  "cliche",
  "topic",
];

export interface ThemeCheck {
  id: ThemeCheckId;
  /** True when the statement passes; false means "look again". */
  ok: boolean;
}

/** Fewer words than this is a topic or a slogan, not a statement about it. */
export const MIN_THEME_WORDS = 8;

const RE_SPLIT_NAMES = /\s*(?:,|;|&|\band\b)\s*/i;
const RE_WORD = /[\p{L}\p{N}'’-]+/gu;
const RE_APOSTROPHE = /[’‘]/g;
const RE_IMPERATIVE_START =
  /^(?:don't|do not|never|always|be|remember|treat|stop|believe|follow|avoid|learn|trust|never|keep|try|dream|respect|listen|think)\b/i;
const RE_SECOND_PERSON = /\b(?:you|your|yours)\b/i;
const RE_OBLIGATION = /\b(?:should|must|ought to|have to|need to)\b/i;
const RE_ABSOLUTE =
  /\b(?:always|never|everyone|everybody|nobody|no one|all people|all humans|every|everything|nothing)\b/i;
const RE_SUMMARY_NOUN =
  /\b(?:the|this) (?:story|novel|book|poem|play|film|movie|text|passage|chapter|author|writer|poet|narrator|protagonist|main character|character)\b/i;
const RE_SEQUENCE = /\b(?:first|then|finally|in the end|at the end)\b/i;

/** Words inside a capitalised name that say nothing about the text: "The Old Man". */
const NAME_STOP_WORDS: ReadonlySet<string> = new Set([
  "the",
  "and",
  "his",
  "her",
  "their",
  "old",
  "young",
]);

const CLICHES: readonly string[] = [
  "honesty is the best policy",
  "love conquers all",
  "never give up",
  "be yourself",
  "money can't buy happiness",
  "don't judge a book by its cover",
  "actions speak louder than words",
  "treat others as you want to be treated",
  "good triumphs over evil",
  "crime doesn't pay",
  "everything happens for a reason",
  "family is everything",
  "what goes around comes around",
  "the grass is always greener",
  "you can't judge",
  "love is blind",
];

export function wordCount(text: string): number {
  return text.match(RE_WORD)?.length ?? 0;
}

/** Character and author names, plus the title, that a general theme should not mention. */
function namedThings(input: ThemeInput): {
  phrases: string[];
  tokens: string[];
} {
  const names = [input.characters, input.author]
    .flatMap((field) => tidy(field).split(RE_SPLIT_NAMES))
    .map(tidy)
    .filter((name) => name !== "");
  const tokens = names
    .flatMap((name) => name.split(" "))
    .filter(
      (token) =>
        token.length >= 3 &&
        token === capitalized(token) &&
        !NAME_STOP_WORDS.has(token.toLowerCase())
    );
  const titleTokens = (tidy(input.title).match(RE_WORD) ?? []).filter(
    (token) => token.length >= 4 && token === capitalized(token)
  );
  const title = tidy(input.title);
  return {
    phrases: title === "" ? [] : [title],
    tokens: [...tokens, ...titleTokens],
  };
}

function capitalized(token: string): string {
  return token.charAt(0).toUpperCase() + token.slice(1);
}

function mentionsNames(statement: string, input: ThemeInput): boolean {
  const { phrases, tokens } = namedThings(input);
  const lowered = statement.toLowerCase();
  if (phrases.some((phrase) => lowered.includes(phrase.toLowerCase()))) {
    return true;
  }
  const words: string[] = statement.match(RE_WORD) ?? [];
  return tokens.some((token) => words.includes(token));
}

function isCommand(statement: string): boolean {
  return (
    RE_IMPERATIVE_START.test(statement) ||
    RE_SECOND_PERSON.test(statement) ||
    RE_OBLIGATION.test(statement)
  );
}

function isCliche(statement: string): boolean {
  const flat = statement.toLowerCase().replace(RE_APOSTROPHE, "'");
  return CLICHES.some((phrase) => flat.includes(phrase));
}

/**
 * Run every check on a statement. `ok: false` is a prompt to look again, not a verdict.
 * The names check needs the title, author, and characters from the input; the topic
 * check only runs when a topic was given.
 */
export function checkTheme(statement: string, input: ThemeInput): ThemeCheck[] {
  const text = tidy(statement);
  const topic = tidy(input.topic).toLowerCase();
  return THEME_CHECK_IDS.map((id) => {
    switch (id) {
      case "length":
        return { id, ok: wordCount(text) >= MIN_THEME_WORDS };
      case "names":
        return { id, ok: !mentionsNames(text, input) };
      case "command":
        return { id, ok: !isCommand(text) };
      case "absolute":
        return { id, ok: !RE_ABSOLUTE.test(text) };
      case "summary":
        return {
          id,
          ok: !(RE_SUMMARY_NOUN.test(text) || RE_SEQUENCE.test(text)),
        };
      case "cliche":
        return { id, ok: !isCliche(text) };
      default:
        return { id, ok: topic === "" || text.toLowerCase().includes(topic) };
    }
  });
}

/** How many checks a statement fails. */
export function flagCount(checks: readonly ThemeCheck[]): number {
  return checks.filter((check) => !check.ok).length;
}
